import { FC, FormEvent, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import BrandDialog from '../../../components/BrandDialog';
import { ON_PRIMARY_ERROR_SX } from '../../../components/fieldStyles';
import { BRAND_DIALOG_CLASS } from '../../../theme';
import { colors, fontWeights } from '../../../theme/tokens';
import {
  ApiError,
  GENERIC_ERROR,
  MetadataPayload,
  assembleDocument,
  updateMetadata,
} from '../../Archive/api';
import Step2Metadata from '../../Archive/Step2Metadata';
import { metadataValuesFrom, validateMetadata } from '../../Archive/metadata';
import { DocumentPage, DraftDocument, MetadataErrors, MetadataValues } from '../../Archive/types';
import { errorText, usePages } from '../../Archive/usePages';
import { useMetadataOptions } from '../../Archive/useMetadataOptions';
import { Notice } from '../../Home/useDocumentActions';
import { legacyPage } from '../useDocumentView';
import PagesTab from './PagesTab';

// The "Modifier le document" modal (no design in the XD, gap 3), over the
// document record at `/document/edit/:id` (see pages/Document/View).
// It replaces the edit page with legacy capture.
//
// Two tabs:
//   Informations  the wizard's "Saisir les données" form
//                 (Step2Metadata), preloaded; "Enregistrer" sends
//                 `PATCH /archives/` and closes the modal with the notice on the
//                 record. The URL de vérification is kept: the
//                 document's, or the one the pages carry if they have it.
//   Pages         the wizard's page management over the archived
//                 document (PagesTab / usePages). Each change is saved
//                 instantly; "Régénérer le PDF" assembles anew, and it is only
//                 enabled when there were changes since the last PDF. Closing
//                 with changes without regenerating asks for confirmation: the
//                 downloadable file would still be the previous one.
//
// The same modal warns inside (Alert) about what happens in the
// "Pages" tab: a Snackbar would sit under the backdrop (see ArchiveWizard).
//
// Mobile (ROADMAP.md, Part 2): BrandDialog turns it into a full-screen
// sheet with the actions pinned at the bottom; the tabs at full width.

export { SESSION_STORAGE_ARCHIVE_EDIT_SUCCESS_KEY } from '../constants';

type EditTab = 'informations' | 'pages';

type Props = {
  open: boolean;
  document: DraftDocument;
  /** Path of the assembled (or single) file, for the legacy document viewer. */
  image: string | null;
  pages: DocumentPage[];
  onClose: () => void;
  /** After "Enregistrer": the record reloads, notifies and closes the modal. */
  onSaved: (notice: Notice) => void;
  /** After regenerating the PDF: the record reloads. */
  onFileChanged: () => void;
};

type DialogNotice = { severity: 'success' | 'error'; text: string };

const TAB_SX = {
  minHeight: 44,
  borderBottom: '1px solid rgba(255, 255, 255, 0.24)',
  '& .MuiTab-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    fontWeight: fontWeights.semiBold,
    textTransform: 'none',
    fontSize: '0.9375rem',
    minHeight: 44,
  },
  '& .MuiTab-root.Mui-selected': { color: colors.white },
  '& .MuiTabs-indicator': { backgroundColor: colors.accent, height: 3 },
} as const;

const EditDocumentDialog: FC<Props> = ({
  open,
  document,
  image,
  pages,
  onClose,
  onSaved,
  onFileChanged,
}) => {
  const [tab, setTab] = useState<EditTab>('informations');
  const [values, setValues] = useState<MetadataValues>(() => metadataValuesFrom(document));
  const [errors, setErrors] = useState<MetadataErrors>({});
  const [formError, setFormError] = useState<string>();
  const [notice, setNotice] = useState<DialogNotice>();
  const [pageIndex, setPageIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [assembling, setAssembling] = useState(false);
  const [confirmStaleOpen, setConfirmStaleOpen] = useState(false);

  const manager = usePages(document.id);
  const { reset: resetPages, version } = manager;
  // The version of the pages the current PDF was generated with.
  const assembledVersionRef = useRef(version);

  const { serviceOptions, issuerOptions } = useMetadataOptions(values.service);

  // On open: the form starts from what the document has, and the
  // pages from what the record already loaded.
  useEffect(() => {
    if (!open) return;
    setTab('informations');
    setValues(metadataValuesFrom(document));
    setErrors({});
    setFormError(undefined);
    setNotice(undefined);
    setPageIndex(0);
    resetPages(pages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) assembledVersionRef.current = version;
    // Only on open: the following versions are changes without regenerating.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isLegacy = manager.pages.length === 0 && Boolean(image);
  const dirty = version !== assembledVersionRef.current;
  const busy = saving || assembling || manager.uploadsInProgress;

  const canRegenerate =
    dirty &&
    !busy &&
    !manager.busy &&
    manager.pages.length > 0;

  // The viewer of the "Informations" tab: the pages, or the single file.
  const viewerPages =
    manager.pages.length > 0
      ? manager.pages
      : image
      ? [legacyPage(document, image)]
      : [];

  const update = (patch: Partial<MetadataValues>) => {
    setValues((current) => ({ ...current, ...patch }));
    setErrors((current) => {
      const next = { ...current };
      (Object.keys(patch) as Array<keyof MetadataValues>).forEach((key) => {
        delete next[key];
      });
      return next;
    });
  };

  // --- Informations -----------------------------------------------------

  const payloadFrom = (source: MetadataValues): Omit<MetadataPayload, 'id'> => ({
    title: source.title.trim(),
    issuing_date: (source.issuingDate as dayjs.Dayjs).format('YYYY-MM-DD'),
    service: source.service,
    issuer: source.issuer.trim(),
    confidential: source.confidential,
    // The document's URL is kept; the pages, if they carry a
    // consistent code, take precedence (it is what the assembly would set).
    verification_url:
      manager.qr.status === 'consistent'
        ? manager.qr.url
        : document.verification_url ?? '',
  });

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (tab !== 'informations' || busy) return;

    const found = validateMetadata(values);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }

    setSaving(true);
    setFormError(undefined);
    try {
      await updateMetadata({ id: document.id, ...payloadFrom(values) });
      onSaved({ severity: 'success', text: 'Document mis à jour avec succès.' });
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : GENERIC_ERROR);
    } finally {
      setSaving(false);
    }
  };

  // --- Pages ------------------------------------------------------------

  const regenerate = async (): Promise<boolean> => {
    setAssembling(true);
    setNotice(undefined);
    try {
      const result = await assembleDocument(document.id);
      assembledVersionRef.current = version;
      setNotice({
        severity: 'success',
        text: `PDF régénéré : ${result.page_count} page${result.page_count > 1 ? 's' : ''}.`,
      });
      onFileChanged();
      return true;
    } catch (err) {
      setNotice({ severity: 'error', text: errorText(err) });
      return false;
    } finally {
      setAssembling(false);
    }
  };

  // --- Closing ----------------------------------------------------------

  const requestClose = () => {
    if (busy) return;
    if (dirty && manager.pages.length > 0) {
      setConfirmStaleOpen(true);
      return;
    }
    onClose();
  };

  const closeWithoutRegenerating = () => {
    setConfirmStaleOpen(false);
    // The pages did change on the server: the record must see them.
    onFileChanged();
    onClose();
  };

  const regenerateAndClose = async () => {
    setConfirmStaleOpen(false);
    setTab('pages');
    if (await regenerate()) onClose();
  };

  // --- Actions ----------------------------------------------------------

  const actions =
    tab === 'informations' ? (
      <>
        <Button variant="outlined" color="inherit" onClick={requestClose} disabled={busy}>
          Annuler
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="secondary"
          disabled={busy}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Enregistrer
        </Button>
      </>
    ) : (
      <>
        <Button variant="outlined" color="inherit" onClick={requestClose} disabled={busy}>
          Fermer
        </Button>
        {!isLegacy && (
          <Button
            variant="contained"
            color="secondary"
            onClick={regenerate}
            disabled={!canRegenerate}
            startIcon={assembling ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Régénérer le PDF
          </Button>
        )}
      </>
    );

  const pageCount = manager.rows.filter((row) => row.kind === 'page' || !row.error).length;

  return (
    <>
      <BrandDialog
        open={open}
        title="Modifier le document"
        onClose={requestClose}
        onSubmit={save}
        busy={busy}
        maxWidth="lg"
        actions={actions}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Tabs
            value={tab}
            onChange={(_, next: EditTab) => setTab(next)}
            variant="fullWidth"
            textColor="inherit"
            aria-label="Sections du document"
            sx={TAB_SX}
          >
            <Tab label="Informations" value="informations" id="edit-tab-informations" />
            <Tab
              label={isLegacy ? 'Fichier' : `Pages (${pageCount})`}
              value="pages"
              id="edit-tab-pages"
              data-testid="edit-tab-pages"
            />
          </Tabs>

          {formError && (
            <Typography role="alert" sx={{ ...ON_PRIMARY_ERROR_SX, fontSize: '0.875rem' }}>
              {formError}
            </Typography>
          )}

          {notice && (
            <Alert
              severity={notice.severity}
              variant="filled"
              role={notice.severity === 'error' ? 'alert' : 'status'}
              onClose={() => setNotice(undefined)}
              sx={{
                whiteSpace: 'pre-line',
                backgroundColor: notice.severity === 'error' ? colors.accent : colors.white,
                color: notice.severity === 'error' ? colors.white : colors.primary,
                '& .MuiAlert-icon, & .MuiAlert-action': {
                  color: notice.severity === 'error' ? colors.white : colors.primary,
                },
              }}
            >
              {notice.text}
            </Alert>
          )}

          {manager.notice && (
            <Alert
              severity="error"
              variant="filled"
              role="alert"
              onClose={manager.clearNotice}
              sx={{
                whiteSpace: 'pre-line',
                backgroundColor: colors.accent,
                color: colors.white,
                '& .MuiAlert-icon, & .MuiAlert-action': { color: colors.white },
              }}
            >
              {manager.notice}
            </Alert>
          )}

          {tab === 'informations' ? (
            <Step2Metadata
              values={values}
              errors={errors}
              onChange={update}
              serviceOptions={serviceOptions}
              issuerOptions={issuerOptions}
              pages={viewerPages}
              qr={isLegacy ? undefined : manager.qr}
              pageIndex={pageIndex}
              onPageIndexChange={setPageIndex}
              disabled={saving}
            />
          ) : (
            <PagesTab
              manager={manager}
              assembling={assembling}
              isLegacy={isLegacy}
            />
          )}
        </Box>
      </BrandDialog>

      {/* Pages changed without regenerating the PDF, with the XD modal pattern */}
      <Dialog
        className={BRAND_DIALOG_CLASS}
        open={confirmStaleOpen}
        onClose={() => setConfirmStaleOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Régénérer le PDF ?</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Les pages ont été modifiées mais le PDF n'a pas été régénéré. Tant
            qu'il ne l'est pas, le fichier téléchargeable reste l'ancien.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" onClick={closeWithoutRegenerating}>
            Fermer sans régénérer
          </Button>
          <Button variant="contained" color="secondary" onClick={regenerateAndClose} autoFocus>
            Régénérer maintenant
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditDocumentDialog;
